/**
 * Payout receiver validation — pure functions.
 *
 * Mirror of backend/src/core/payments/withdrawals/payout-validation.ts — keep in sync.
 */

/** The gateway's SEPA-OUT sandbox account. Not an IBAN; accepted only against a test gateway. */
export const SEPA_TEST_ACCOUNT = '1234567890';

export function normaliseCardNumber(value: string): string {
  return value.replace(/[\s-]/g, '');
}

/** Luhn check over 13–19 digits. */
export function isValidCardNumber(value: string): boolean {
  if (!/^\d{13,19}$/.test(value)) return false;
  let sum = 0;
  for (let i = 0; i < value.length; i++) {
    let d = Number(value[value.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return sum % 10 === 0;
}

export function cardBrand(value: string): 'VISA' | 'MASTERCARD' | 'MAESTRO' | 'OTHER' {
  if (/^4/.test(value)) return 'VISA';
  if (/^(5[1-5]|222[1-9]|22[3-9]\d|2[3-6]\d\d|27[01]\d|2720)/.test(value)) return 'MASTERCARD';
  if (/^(50|5[6-9]|6)/.test(value)) return 'MAESTRO';
  return 'OTHER';
}

export function normaliseIban(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

/** Structure + ISO 13616 mod-97 check. */
export function isValidIban(value: string): boolean {
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(value)) return false;
  const rearranged = value.slice(4) + value.slice(0, 4);
  let remainder = 0;
  for (const ch of rearranged) {
    const digits = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch;
    for (const d of digits) remainder = (remainder * 10 + Number(d)) % 97;
  }
  return remainder === 1;
}

export function ibanCountry(iban: string): string | undefined {
  return /^[A-Z]{2}\d{2}/.test(iban) ? iban.slice(0, 2) : undefined;
}

export function maskIban(iban: string): string {
  return iban.length <= 8 ? `•••• ${iban.slice(-4)}` : `${iban.slice(0, 4)} •••• ${iban.slice(-4)}`;
}

// ─── Frontend-only input formatters ─────────────────────────────────

/** Live IBAN input: alphanumerics only, upper-case, max 34, groups of 4. */
export function formatIbanInput(value: string): string {
  const raw = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 34);
  return raw.replace(/(.{4})/g, '$1 ').trim();
}

/** Live card input: digits only, max 19, groups of 4. */
export function formatCardInput(value: string): string {
  const raw = value.replace(/\D/g, '').slice(0, 19);
  return raw.replace(/(.{4})/g, '$1 ').trim();
}
